(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const setupStory = (story) => {
    if (story.dataset.peakScrollReady === 'true') return;
    story.dataset.peakScrollReady = 'true';
    const canvas = story.querySelector('[data-peak-scroll-canvas]');
    const frameData = story.querySelector('[data-peak-scroll-frames]');
    const progressBar = story.querySelector('[data-peak-scroll-progress]');
    if (!canvas || !frameData || !progressBar) return;

    let urls;
    try {
      urls = JSON.parse(frameData.textContent);
    } catch (_error) {
      return;
    }
    if (!Array.isArray(urls) || !urls.length) return;

    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const saveData = Boolean(connection && connection.saveData);
    const slowConnection = Boolean(connection && /(^|-)2g$/.test(connection.effectiveType || ''));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const staticOnly = saveData || reducedMotion;
    const cache = new Map();
    const pending = new Set();
    const queued = new Set();
    const retryCounts = new Map();
    const queue = [];
    let activeLoads = 0;
    let currentTarget = 0;
    let lastDrawn = -1;
    let ticking = false;
    let initialized = false;
    let warming = false;

    if (staticOnly) story.dataset.static = 'true';

    const resizeCanvas = () => {
      const bounds = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(1, Math.round(bounds.width * pixelRatio));
      const height = Math.max(1, Math.round(bounds.height * pixelRatio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      lastDrawn = -1;
      drawNearest(currentTarget);
    };

    const drawImage = (image, index) => {
      if (!image || !image.complete || !image.naturalWidth) return false;
      const width = canvas.width;
      const height = canvas.height;
      const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      const drawWidth = image.naturalWidth * scale;
      const drawHeight = image.naturalHeight * scale;
      const x = (width - drawWidth) / 2;
      const y = (height - drawHeight) / 2;
      context.fillStyle = '#020c16';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, x, y, drawWidth, drawHeight);
      lastDrawn = index;
      story.classList.remove('is-loading');
      story.classList.add('is-enhanced', 'is-ready');
      return true;
    };

    const drawNearest = (target) => {
      const exact = cache.get(target);
      if (exact && drawImage(exact, target)) return;
      const ready = [...cache.entries()]
        .filter(([, image]) => image.complete && image.naturalWidth)
        .sort((a, b) => Math.abs(a[0] - target) - Math.abs(b[0] - target));
      if (ready.length) drawImage(ready[0][1], ready[0][0]);
    };

    const pruneCache = () => {
      if (cache.size <= 14) return;
      [...cache.keys()]
        .filter((index) => !pending.has(index) && index !== currentTarget)
        .sort((a, b) => Math.abs(b - currentTarget) - Math.abs(a - currentTarget))
        .slice(0, cache.size - 14)
        .forEach((index) => cache.delete(index));
    };

    const pumpQueue = () => {
      while (activeLoads < 3 && queue.length) {
        const index = queue.shift();
        queued.delete(index);
        if (cache.has(index) || (Math.abs(index - currentTarget) > 12 && index !== 0)) continue;

        const image = new Image();
        image.decoding = 'async';
        cache.set(index, image);
        pending.add(index);
        activeLoads += 1;

        const finish = (loaded) => {
          pending.delete(index);
          activeLoads -= 1;
          if (!loaded) {
            cache.delete(index);
            const attempts = retryCounts.get(index) || 0;
            retryCounts.set(index, attempts + 1);
            if (attempts < 1 && Math.abs(index - currentTarget) <= 8) {
              window.setTimeout(() => requestFrame(index), 400);
            }
            if (index === 0 && lastDrawn < 0) story.classList.remove('is-loading');
          } else {
            retryCounts.delete(index);
          }
          if (Math.abs(index - currentTarget) <= 1 || lastDrawn < 0) drawNearest(currentTarget);
          pruneCache();
          pumpQueue();
        };
        image.addEventListener('load', () => {
          if (typeof image.decode === 'function') {
            image.decode().then(() => finish(true), () => finish(true));
          } else {
            finish(true);
          }
        }, { once: true });
        image.addEventListener('error', () => finish(false), { once: true });
        image.src = urls[index];
      }
    };

    const requestFrame = (index, priority = false) => {
      if (index < 0 || index >= urls.length || cache.has(index) || queued.has(index)) return;
      queued.add(index);
      if (priority) queue.unshift(index);
      else queue.push(index);
      pumpQueue();
    };

    const prepareAround = (target) => {
      requestFrame(target, true);
      if (staticOnly) return;
      for (let distance = 1; distance <= 5; distance += 1) {
        requestFrame(target + distance);
        requestFrame(target - distance);
      }
    };

    const warmNetworkCache = () => {
      if (warming || staticOnly || slowConnection || !window.fetch) return;
      warming = true;
      const order = [];
      const added = new Set([0]);
      // Warm sparse keyframes only. The prior 4/2/1 passes fetched every frame,
      // creating 57 early requests before the shopper reached this section.
      // Neighbouring frames continue to load on demand in prepareAround().
      for (let index = 4; index < urls.length; index += 4) {
        if (!added.has(index)) {
          added.add(index);
          order.push(index);
        }
      }
      const lastIndex = urls.length - 1;
      if (!added.has(lastIndex)) order.push(lastIndex);
      let cursor = 0;
      const worker = async () => {
        while (cursor < order.length) {
          const index = order[cursor];
          cursor += 1;
          try {
            const response = await fetch(urls[index], {
              cache: 'force-cache',
              headers: { Accept: 'image/avif,image/webp,image/*,*/*;q=0.8' },
            });
            if (response.ok) await response.blob();
          } catch (_error) {
            // On-demand image loading remains the fallback.
          }
        }
      };
      const start = () => Promise.all([worker(), worker()]);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout: 1200 });
      } else {
        window.setTimeout(start, 350);
      }
    };

    const update = () => {
      ticking = false;
      const bounds = story.getBoundingClientRect();
      const distance = Math.max(1, bounds.height - window.innerHeight);
      const progress = staticOnly ? 0 : Math.min(1, Math.max(0, -bounds.top / distance));
      const target = Math.round(progress * (urls.length - 1));
      progressBar.style.transform = `scaleX(${progress})`;
      if (target !== currentTarget || lastDrawn < 0) {
        currentTarget = target;
        prepareAround(target);
        drawNearest(target);
      }
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const initialize = () => {
      if (initialized) return;
      initialized = true;
      story.classList.add('is-loading');
      resizeCanvas();
      requestFrame(0, true);
      warmNetworkCache();
      if (!staticOnly) {
        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', resizeCanvas, { passive: true });
      }
      requestUpdate();
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          initialize();
          observer.disconnect();
        }
      }, { rootMargin: '45% 0px' });
      observer.observe(story);
    } else {
      initialize();
    }
  };

  const initializeMobileStories = () => {
    if (!mobileQuery.matches) return;
    document.querySelectorAll('[data-peak-scroll-story]').forEach(setupStory);
  };

  initializeMobileStories();
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', initializeMobileStories);
  }
})();
